import React from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { ArrowLeft } from "lucide-react";
import SEO from "../components/SEO";

const LAST_UPDATED_ISO = "2026-07-23";
const LAST_UPDATED_LABEL = "23 July 2026";

const Section = ({ title, children }) => (
  <section className="mt-6" data-testid={`privacy-section-${title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
    <h2 className="text-base font-semibold text-white">{title}</h2>
    <div className="mt-2 text-sm text-zinc-300 leading-relaxed space-y-2">{children}</div>
  </section>
);

export default function Privacy() {
  return (
    <div
      className="min-h-screen bg-[#0b0b0c] text-white"
      style={{ paddingTop: "env(safe-area-inset-top)", paddingBottom: "env(safe-area-inset-bottom)" }}
      data-testid="privacy-screen"
    >
      <SEO
        title="Privacy Policy"
        description="FreeFitFuel Privacy Policy — what we collect, how we use it, who processes it and how to exercise your UK GDPR rights."
        path="/privacy"
      />
      <Helmet>
        <meta name="revised" content={LAST_UPDATED_ISO} />
        <meta name="last-modified" content={LAST_UPDATED_ISO} />
      </Helmet>
      <div className="mx-auto max-w-2xl px-5 py-6 pb-24">
        <div className="flex items-center gap-3 mb-6">
          <Link to="/" className="h-9 w-9 rounded-full grid place-items-center bg-zinc-900 hover:bg-zinc-800" data-testid="privacy-back-home">
            <ArrowLeft className="h-4 w-4" />
          </Link>
          <h1 className="text-xl font-semibold tracking-tight">Privacy Policy</h1>
        </div>

        <div
          data-testid="privacy-last-updated"
          className="mt-2 inline-flex items-center gap-2 rounded-full border border-[#BEFF38]/30 bg-[#BEFF38]/10 px-3 py-1.5"
        >
          <span className="text-[10px] uppercase tracking-[0.16em] font-black text-[#BEFF38]">Last updated</span>
          <time dateTime={LAST_UPDATED_ISO} className="text-xs font-semibold text-white">{LAST_UPDATED_LABEL}</time>
        </div>

        <Section title="Who we are">
          <p>FreeFitFuel ("we", "us", "our") provides an adaptive fitness, nutrition and wellbeing web application at freefitfuel.com and freefitfuel.app. This policy explains what we collect, why, and the choices you have. FreeFitFuel is currently offered as a mobile-friendly website.</p>
        </Section>

        <Section title="What we collect">
          <ul className="list-disc pl-5 space-y-1">
            <li><strong>Anonymous session identifier</strong> — a random ID generated in your browser and used to associate your plan and progress with your device. We do not require you to create a named account.</li>
            <li><strong>Email address</strong> — collected only when you subscribe (via Stripe checkout) or contact support. Used to send receipts, allow you to recover Pro access on a new device, and reply to support enquiries.</li>
            <li><strong>Plan inputs</strong> — goal, sex, age, height, weight, activity, injuries, and equipment that you enter when building your plan.</li>
            <li><strong>Training data</strong> — check-ins, lift logs, workouts, meal plans and progress you create in the app.</li>
            <li><strong>Food photos</strong> — images you submit to the Food Snap feature are sent to our AI provider (via the Emergent LLM key) for macro analysis. Images are not persisted server-side after the response is returned.</li>
            <li><strong>AI Coach messages</strong> — your chat history with the in-app coach, stored so the coach can maintain context across sessions.</li>
            <li><strong>Subscription state</strong> — plan tier, renewal date and payment status, sourced from Stripe.</li>
            <li><strong>Anonymous analytics events</strong> — page views and funnel events (e.g. "started signup") stored without personally identifying information, used only to understand which features get used.</li>
          </ul>
        </Section>

        <Section title="Why we collect it">
          <p>We use this information solely to deliver the service — to personalise your workouts, generate nutrition advice, track progress, verify your subscription, and improve the product. We do not sell your data, do not run advertising, and do not perform cross-app tracking.</p>
        </Section>

        <Section title="Where it lives">
          <p>Your plan, training data, chat history and analytics events are stored in a managed MongoDB database hosted in the European Union. Payment metadata is stored securely by Stripe. AI generations are processed by OpenAI and Google (Nano Banana image model) via the Emergent universal LLM gateway and are not used to train third-party models.</p>
        </Section>

        <Section title="Third-party processors">
          <ul className="list-disc pl-5 space-y-1">
            <li><strong>Stripe</strong> — payment processing, subscription management, refunds. Stripe is our sub-processor for card data.</li>
            <li><strong>Emergent (LLM gateway)</strong> — routes AI requests to OpenAI (text + Food Snap vision) and Google Gemini (meal image generation).</li>
            <li><strong>MongoDB Atlas (EU region)</strong> — encrypted database hosting.</li>
            <li><strong>Cloudflare</strong> — CDN and DDoS protection for the website.</li>
          </ul>
          <p>Each processor operates under its own privacy policy and applies appropriate contractual and technical safeguards for personal data.</p>
        </Section>

        <Section title="Cookies and browser storage">
          <p>We use only strictly-necessary cookies and localStorage keys — for example to remember your session, keep you signed in to Pro after payment, and persist your last plan. We do not use advertising or tracking cookies.</p>
        </Section>

        <Section title="How long we keep it">
          <p>Plan data, training logs and AI chat history are retained while your account/session is active and for up to 12 months after your last activity, after which anonymised data may be kept for aggregate product analytics. Stripe retains billing records for the period required by tax and financial regulation (typically 7 years). You can request server-side deletion at any time by emailing us at <a href="mailto:gcanth@gmail.com" className="text-lime-400 underline">gcanth@gmail.com</a> with the email address used at checkout or your session ID.</p>
        </Section>

        <Section title="Your rights">
          <p>Under UK GDPR and the UK Data Protection Act 2018 you have the right to access, correct, or delete your personal data, to object to processing, to restrict processing, and to data portability. You can also lodge a complaint with the UK Information Commissioner's Office (ico.org.uk) if you believe we have mishandled your data. Email <a href="mailto:gcanth@gmail.com" className="text-lime-400 underline">gcanth@gmail.com</a> to exercise any of these rights and we will respond within 30 days.</p>
        </Section>

        <Section title="Children">
          <p>FreeFitFuel is not intended for users under 16. We do not knowingly process data from children.</p>
        </Section>

        <Section title="Changes">
          <p>If we make material changes to this policy we'll surface a notice on the site before the changes take effect. The "Last updated" date at the top of this page always reflects the current version.</p>
        </Section>

        <Section title="Contact">
          <p>Questions or concerns: <a href="mailto:gcanth@gmail.com" className="text-lime-400 underline">gcanth@gmail.com</a></p>
        </Section>
      </div>
    </div>
  );
}
